import React, { useId, useMemo, useState } from 'react';

import type { PopoverProps } from '@mui/material';
import { Divider, List, ListItemButton, Popover, Skeleton, Typography } from '@mui/material';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import { uniqBy } from 'ramda';
import InfiniteScroll from 'react-infinite-scroll-component';
import { SearchInput } from 'src/components';
import { AlineCheckbox, AlineTooltip } from 'src/components/generic';
import { SkinnyPlusIcon } from 'src/components/icons/SkinnyPlusIcon';
import { createPendingParty } from 'src/components/parties/pending-party';
import type { PartiesEditorRow } from 'src/components/workflow/parties-editor-rows';
import { getPartiesEditorRows } from 'src/components/workflow/parties-editor-rows';
import { useSelectCurrentUser } from 'src/gql';
import type { Apollo } from 'src/gql/generated';
import type { WorkflowFileSubset } from 'src/hooks';
import { useGetCheckWorkflowAuthorization, usePartySearch, useSetWorkflowParties } from 'src/hooks';
import type { UserWithOrg } from 'src/types';
import type { WorkflowPermissionSubset } from 'src/types/apollo/workflow-permission';
import {
  labelPopoverMaxHeightPx,
  labelPopoverWidthPx,
  smallCheckboxHeightPx,
  smallCheckboxWidthPx,
} from 'src/workflow/labels';
import { makeStyles } from 'tss-react/mui';

import { labelPopoverOffsetPx } from './workflow-list';

export type TextColorType =
  | 'initial'
  | 'inherit'
  | 'primary'
  | 'secondary'
  | 'textPrimary'
  | 'textSecondary'
  | 'error';

const numDisplayedParties = 4;
const useStyles = makeStyles()(theme => ({
  ghostPartyText: {
    '&:hover': {
      color: theme.palette.primary.main,
      textDecorationLine: 'underline',
      textDecorationStyle: 'solid',
    },
    cursor: 'pointer',
    textDecorationLine: 'underline',
    textDecorationStyle: 'dashed',
  },
  partyName: {
    '&:hover': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
    cursor: 'pointer',
  },
  partyNameList: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
  },
  popover: {
    padding: theme.spacing(2),
    width: labelPopoverWidthPx,
  },
  spacingRight: {
    marginRight: theme.spacing(0.5),
  },
}));

interface PartiesListBodyProps {
  parties: PartySubset[];
  setOpen: (open: boolean) => void;
  textColor?: TextColorType;
}

export function PartiesListBody({
  parties,
  setOpen,
  textColor = 'textSecondary',
}: PartiesListBodyProps) {
  const { classes } = useStyles();
  const sortedParties = [...parties].sort((a, b) => a.name.localeCompare(b.name));
  const displayedParties = sortedParties.slice(0, numDisplayedParties);
  const remainingCount = Math.max(0, parties.length - numDisplayedParties);

  if (!parties.length) {
    return (
      <Typography
        noWrap
        className={classes.ghostPartyText}
        color={textColor}
        onClick={e => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        Add Party +
      </Typography>
    );
  }

  return (
    <>
      {displayedParties.map((party, index) => {
        const isLast = index === displayedParties.length - 1 && remainingCount === 0;

        return (
          <React.Fragment key={party.id}>
            <Typography
              noWrap
              className={clsx(classes.partyName, !isLast && classes.spacingRight)}
              color={textColor}
              variant="body2"
              onClick={e => {
                e.stopPropagation();
                setOpen(true);
              }}
            >
              {isLast ? party.name : `${party.name},`}
            </Typography>
          </React.Fragment>
        );
      })}
      {remainingCount > 0 && (
        <Typography
          noWrap
          className={classes.partyName}
          color={textColor}
          variant="body2"
          onClick={e => {
            e.stopPropagation();
            setOpen(true);
          }}
        >
          + {remainingCount} {remainingCount === 1 ? 'party' : 'parties'}
        </Typography>
      )}
    </>
  );
}

interface WorkflowWithParties extends Pick<Apollo.Workflow, 'id' | '__typename'> {
  files: WorkflowFileSubset[];
  owner: UserWithOrg;
  parties: PartySubset[];
  permissions: WorkflowPermissionSubset[];
}

export function WorkflowPartiesList({
  textColor = 'textSecondary',
  workflow,
}: {
  workflow: WorkflowWithParties;
  textColor?: TextColorType;
}) {
  const { classes } = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <div ref={setAnchorEl} className={classes.partyNameList}>
      <PartiesListBody parties={workflow.parties} setOpen={setOpen} textColor={textColor} />
      <WorkflowPartiesPopover
        anchorEl={anchorEl}
        open={open}
        setOpen={setOpen}
        workflow={workflow}
      />
    </div>
  );
}

export function WorkflowPartiesPopover({
  anchorEl,
  open,
  setOpen,
  workflow,
}: {
  anchorEl: Element | null;
  open: boolean;
  setOpen: (value: boolean) => void;
  workflow: WorkflowWithParties;
}) {
  const currentUser = useSelectCurrentUser();
  const { setWorkflowParties } = useSetWorkflowParties();
  const { checkWorkflowAuthorization } = useGetCheckWorkflowAuthorization();
  const { canEditWorkflow } = checkWorkflowAuthorization(workflow, currentUser);

  async function handlePartyToggle(party: PartySubset) {
    const newPartyIds = new Set(workflow.parties.map(p => p.id));

    if (newPartyIds.has(party.id)) newPartyIds.delete(party.id);
    else newPartyIds.add(party.id);

    await setWorkflowParties({
      files: workflow.files,
      parties: uniqBy(
        p => p.id,
        [...workflow.parties, party].filter(p => newPartyIds.has(p.id))
      ),
      workflowId: workflow.id,
    });
  }

  return (
    <PartiesEditor
      disabledReason={
        !canEditWorkflow
          ? 'Only the workflow owner and editors can edit parties'
          : !workflow.files.length
            ? 'No documents added to workflow'
            : ''
      }
      popoverProps={{
        anchorEl,
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
        onClose: () => setOpen(false),
        open,
        style: { transform: `translateX(${labelPopoverOffsetPx}px)` },
        transformOrigin: { horizontal: 'left', vertical: 'top' },
      }}
      selectedParties={workflow.parties}
      onPartyToggle={handlePartyToggle}
    />
  );
}

const usePartyEditorStyles = makeStyles<void, 'listItem'>()((theme, _params, classes) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: labelPopoverMaxHeightPx,
    overflow: 'hidden',
  },
  footer: {
    flexShrink: 0,
    padding: theme.spacing(1),
  },
  footerDivider: {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  ghostCheckbox: {
    alignItems: 'center',
    display: 'flex',
    height: smallCheckboxHeightPx,
    justifyContent: 'center',
    width: smallCheckboxWidthPx,
  },
  hiddenCheckbox: {
    [`.${classes.listItem}:hover &, input:checked ~ *`]: {
      visibility: 'visible',
    },
    boxSizing: 'border-box',
    height: smallCheckboxHeightPx,
    visibility: 'hidden',
    width: smallCheckboxWidthPx,
  },
  labelList: {
    padding: theme.spacing(1),
  },
  listItem: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
  },
  listScroll: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
  },
  paper: {
    maxHeight: labelPopoverMaxHeightPx,
    width: labelPopoverWidthPx,
  },
  pendingName: {
    color: theme.palette.text.secondary,
  },
  pinnedDivider: {
    margin: theme.spacing(0.5, 1),
  },
  searchContainer: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    padding: theme.spacing(1),
  },
}));

export type PartySubset = Pick<Apollo.Party, 'id' | 'name' | '__typename'>;
export interface PartyEditorPopoverProps extends Required<
  Pick<PopoverProps, 'anchorEl' | 'anchorOrigin' | 'onClose' | 'open' | 'transformOrigin'>
> {
  style?: React.CSSProperties;
}
interface PartiesEditorContentProps {
  disabledReason: string;
  /**
   * Adds the party to the selection, or removes it. A pending party (see
   * `createPendingParty`) is created by the same mutation that selects it.
   */
  onPartyToggle: (party: PartySubset) => Promise<void>;
  selectedParties: PartySubset[];
}
interface PartiesEditorProps extends Omit<PartiesEditorContentProps, 'disabledReason'> {
  popoverProps: PartyEditorPopoverProps;
  disabledReason?: string;
}

/**
 * The popover unmounts its content when closed, so every open starts a fresh
 * session: an empty search and the selection pinned as it was at open.
 */
export function PartiesEditor({ disabledReason = '', popoverProps, ...props }: PartiesEditorProps) {
  const { classes } = usePartyEditorStyles();

  return (
    <Popover
      classes={{ paper: classes.paper }}
      onClick={e => e.stopPropagation()}
      {...popoverProps}
    >
      <PartiesEditorContent disabledReason={disabledReason} {...props} />
    </Popover>
  );
}

function PartiesEditorContent({
  disabledReason,
  onPartyToggle,
  selectedParties,
}: PartiesEditorContentProps) {
  const { classes } = usePartyEditorStyles();
  const { enqueueSnackbar } = useSnackbar();
  const {
    hasMore,
    loadMore,
    parties: searchResults,
    resultsSearch,
    search: searchText,
    setSearch: setSearchText,
  } = usePartySearch();
  const partyListScrollId = `party-popover-${useId().replace(/:/g, '')}`;
  const [pinnedParties] = useState(() =>
    [...selectedParties].sort((a, b) => a.name.localeCompare(b.name))
  );
  const [createdParties, setCreatedParties] = useState<PartySubset[]>([]);
  const { exactMatch, isStale, pinnedRows, resultRows } = useMemo(
    () =>
      getPartiesEditorRows({
        createdParties,
        pinnedParties,
        resultsSearch,
        search: searchText,
        searchResults,
        selectedParties,
      }),
    [createdParties, pinnedParties, resultsSearch, searchText, searchResults, selectedParties]
  );
  const isDisabled = Boolean(disabledReason);
  const hasRows = Boolean(pinnedRows.length || resultRows.length);
  const trimmedSearch = searchText.trim();
  const canCreate = Boolean(trimmedSearch) && !exactMatch;
  const isInitialLoad = resultsSearch === null;

  async function handleCreateParty() {
    if (isDisabled || !canCreate) return;

    const pendingParty = createPendingParty(trimmedSearch);
    setCreatedParties(previous => [...previous, pendingParty]);
    setSearchText('');
    try {
      await onPartyToggle(pendingParty);
    } catch (error) {
      console.error('Error creating party:', error);
      setCreatedParties(previous => previous.filter(party => party.id !== pendingParty.id));
      enqueueSnackbar(`Couldn't add "${pendingParty.name}".`, { variant: 'error' });
    }
  }

  function handleSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter' || isDisabled) return;

    event.preventDefault();
    if (canCreate) void handleCreateParty();
    else if (exactMatch) void onPartyToggle(exactMatch.party);
  }

  return (
    <div className={classes.container}>
      <div className={classes.searchContainer}>
        <SearchInput
          placeholder="Search or add parties..."
          search={searchText}
          setSearch={setSearchText}
          onKeyDown={handleSearchKeyDown}
        />
      </div>
      {hasRows && (
        <div className={classes.listScroll} id={partyListScrollId}>
          <InfiniteScroll
            dataLength={resultRows.length}
            hasMore={hasMore && !isStale}
            loader={<PartyRowSkeletons count={1} rowClassName={classes.listItem} />}
            next={loadMore}
            scrollableTarget={partyListScrollId}
          >
            <List className={classes.labelList}>
              {pinnedRows.map(row => (
                <PartyRow
                  key={row.party.id}
                  disabledReason={disabledReason}
                  row={row}
                  onPartyToggle={onPartyToggle}
                />
              ))}
              {Boolean(pinnedRows.length && resultRows.length) && (
                <Divider className={classes.pinnedDivider} component="li" />
              )}
              {resultRows.map(row => (
                <PartyRow
                  key={row.party.id}
                  disabledReason={disabledReason}
                  row={row}
                  onPartyToggle={onPartyToggle}
                />
              ))}
              {isInitialLoad && <PartyRowSkeletons count={3} rowClassName={classes.listItem} />}
            </List>
          </InfiniteScroll>
        </div>
      )}
      {isInitialLoad && !hasRows && (
        <List className={classes.labelList}>
          <PartyRowSkeletons count={5} rowClassName={classes.listItem} />
        </List>
      )}
      {canCreate && (
        <div className={clsx(classes.footer, hasRows && classes.footerDivider)}>
          <DisabledReasonTooltip disabledReason={disabledReason}>
            <ListItemButton
              className={classes.listItem}
              disabled={isDisabled}
              onClick={handleCreateParty}
            >
              <div className={classes.ghostCheckbox}>
                <SkinnyPlusIcon />
              </div>
              <Typography noWrap>{`Create "${trimmedSearch}" party`}</Typography>
            </ListItemButton>
          </DisabledReasonTooltip>
        </div>
      )}
    </div>
  );
}

function PartyRow({
  disabledReason,
  onPartyToggle,
  row,
}: {
  disabledReason: string;
  onPartyToggle: (party: PartySubset) => Promise<void>;
  row: PartiesEditorRow;
}) {
  const { classes } = usePartyEditorStyles();
  const isDisabled = Boolean(disabledReason);

  return (
    <DisabledReasonTooltip disabledReason={disabledReason}>
      <ListItemButton
        className={classes.listItem}
        disabled={isDisabled}
        onClick={() => void onPartyToggle(row.party)}
      >
        <AlineCheckbox
          checked={row.checked}
          className={classes.hiddenCheckbox}
          disabled={isDisabled}
          size="small"
        />
        <Typography noWrap className={clsx(row.pending && classes.pendingName)}>
          {row.party.name}
        </Typography>
      </ListItemButton>
    </DisabledReasonTooltip>
  );
}

/** Only mounts a tooltip when there is a reason to show, so long lists stay cheap. */
function DisabledReasonTooltip({
  children,
  disabledReason,
}: {
  children: React.ReactElement;
  disabledReason: string;
}) {
  if (!disabledReason) return children;

  return (
    <AlineTooltip arrow placement="top" title={disabledReason}>
      <div>{children}</div>
    </AlineTooltip>
  );
}

/** Placeholder rows shown while parties load (initial fetch and load-more). */
function PartyRowSkeletons({
  count,
  rowClassName,
}: {
  count: number;
  rowClassName: string;
}): JSX.Element {
  return (
    <>
      {Array.from({ length: count }, (_unused, index) => (
        <div key={index} className={rowClassName}>
          <Skeleton variant="text" width="70%" />
        </div>
      ))}
    </>
  );
}
